import { useState } from 'react'
import { Alert, Form, Input, Modal, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { activateVoucher } from './voucherApi'

interface VoucherActivateModalProps {
  open: boolean
  /** Oldindan to'ldiriladigan kod — kartalar ro'yxatidan ochilganda */
  code?: string | null
  onClose: () => void
}

interface FormValues {
  code: string
  login: string
}

/**
 * Kartani ISHLATISH — panel tomonidagi yagona yo'l.
 *
 * <h2>Nega bu oyna kerak bo'ldi</h2>
 * Karta chiqarish, chop etish, sotish va bekor qilish panelda bor edi,
 * ISHLATISH esa yo'q. Ya'ni oxirgi bosqichda operatorning qo'li bo'sh
 * qolardi va mijozga «kodni o'zingiz kiritasiz» deb aytishdan boshqa
 * yo'l yo'q edi — mehmon portali esa faqat hotspot zonasida ochiladi,
 * PPPoE kartasi uchun umuman ishlamaydi.
 *
 * <h2>⚠️ Bu YANGI HUQUQ QOIDASI EMAS</h2>
 * `POST /api/vouchers/activate` allaqachon `subscriber.create` talab
 * qiladi va bu o'zgarmadi. Tugma aynan shu huquq bilan ko'rsatiladi,
 * ya'ni panel serverdan na yumshoqroq, na qattiqroq bo'ladi. Kimning
 * huquqi borligi (`AGENT` da yo'q) — bu OYNANING savoli emas, rol
 * shablonining savoli.
 *
 * <h2>⚠️ Login MAJBURIY va bu ataylab</h2>
 * Backend `login` bo'yicha abonentni TOPADI; topilmasa amal rad etiladi
 * (`VoucherServiceImpl.requireExisting`, egasi qarori 2026-08-23 —
 * ilgari u YANGISINI ocharDI). Maydonni ixtiyoriy
 * qilish «karta o'zi abonentga aylanadi» degan taassurot berardi —
 * `docs/01-DOMAIN-MODEL.md` aynan shu modelni RAD ETADI: bir odam bir
 * necha karta ishlatishi mumkin va tarix bitta hisobda turishi kerak.
 *
 * <h2>⚠️ Xato OYNANING ICHIDA</h2>
 * Ikki marta ishlatishga urinish 409 beradi va javobda kartaning holati
 * bo'ladi («ishlatilgan», «muddati o'tgan», «bekor qilingan» — uch xil
 * vaziyat). Aynan shu xabar kod terilgan maydon bilan bir ekranda
 * turishi kerak, aks holda operator kodni qayta o'qishga majbur bo'ladi.
 */
export function VoucherActivateModal({ open, code, onClose }: VoucherActivateModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  /** Muvaffaqiyatli natija — oyna YOPILMAYDI, xabar shu yerda qoladi */
  const [done, setDone] = useState<{ code: string; login: string } | null>(null)

  const activate = useMutation({
    mutationFn: (values: FormValues) => activateVoucher(values.code, values.login),
    onSuccess: (voucher, values) => {
      /*
        ⚠️ IKKI OILA kesh bekor qilinadi va ikkalasi ham majburiy:
        kartaning holati (`vouchers.*` — ro'yxat ham, partiya sanog'i
        ham) va ABONENT (`subscribers.*` — yangi hisob yaratilgan
        bo'lishi va grant qo'shilgan bo'lishi mumkin). Ikkinchisini
        unutish eng oson va u JIMGINA eskiradi: abonentlar ro'yxatida
        yangi qator umuman ko'rinmasdi.
      */
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.codes(voucher.batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      setDone({ code: voucher.code, login: values.login.trim() })
    },
  })

  const close = () => {
    activate.reset()
    setDone(null)
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('voucher.activateTitle')}
      onCancel={close}
      onOk={done ? close : () => submitOnValid(form, (values) => activate.mutate(values))}
      okText={done ? t('common.close') : t('voucher.activate')}
      cancelText={t('common.cancel')}
      confirmLoading={activate.isPending}
      destroyOnHidden
      width={520}
    >
      {done ? (
        // ⚠️ `success`: bu amal bazani ham, abonentni ham TO'LIQ
        // o'zgartiradi — bu yerda «faol sessiyaga qo'llanmadi» degan
        // yarim holat YO'Q (karta yangi grant beradi, mavjud sessiyani
        // o'zgartirmaydi va o'zgartirishi ham kerak emas).
        <Alert
          type="success"
          showIcon
          message={t('voucher.activateDone', { code: done.code, login: done.login })}
        />
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            {t('voucher.activateHint')}
          </Typography.Paragraph>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('voucher.activateNote')}
          />

          {/* Xato SHU YERDA qoladi — kod terilgan maydon bilan bir ekranda */}
          {activate.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={translateError(activate.error, t)}
            />
          ) : null}

          <Form<FormValues>
            form={form}
            layout="vertical"
            preserve={false}
            disabled={activate.isPending}
            initialValues={{ code: code ?? '' }}
          >
            <Form.Item
              name="code"
              label={t('voucher.code')}
              rules={[{ required: true, message: t('voucher.activateCodeRequired') }]}
            >
              {/* ⚠️ Kod BOSH HARFDA saqlanadi (`VoucherCodeGenerator`), shuning
                  uchun kiritish ham darhol bosh harfga o'tkaziladi — mehmon
                  portalidagi bilan bir xil qoida (`VoucherStep`). */}
              <Input autoFocus onInput={(e) => {
                const el = e.currentTarget
                el.value = el.value.toUpperCase()
              }} />
            </Form.Item>

            <Form.Item
              name="login"
              label={t('voucher.activateLogin')}
              extra={t('voucher.activateLoginHint')}
              rules={[{ required: true, message: t('voucher.activateLoginRequired') }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  )
}
